import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import concat from 'lodash/concat';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';

import WebAPIClient, { errorResponseToastr } from '../../../../api';
import { displaykW } from '../../../../helpers/display-energy';
import { calculateTotals, compileChartData, prepareCsvData } from './helpers';
import ProductionProfileChart from '../../../../components/charts/analysis/ProductionProfileChart';
import SelectMeters from '../../../../components/charts/selectors/SelectMeters';
import SelectInverters from '../../../../components/charts/selectors/SelectInverters';
import SelectRange, {
  RANGES,
} from '../../../../components/charts/selectors/SelectRange';
import SelectTimezone, {
  TIMEZONES,
} from '../../../../components/charts/selectors/SelectTimezone';
import SelectInterval, {
  INTERVALS,
} from '../../../../components/charts/selectors/SelectInterval';
import SelectUnit, {
  UNITS,
} from '../../../../components/charts/selectors/SelectUnits';
import GenerateCSVButton from '../../../../components/charts/buttons/GenerateCSVButton';
import ChartHeader from '../ChartHeader';
import { toastr } from '../../../CustomToast';

function ProductionProfileChartContainer(props) {
  const { defaultTimezone, orgId, meters, inverters } = props;
  const timeseriesData = useSelector((state) => state.timeseries.data);

  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [totals, setTotals] = useState({ meter: 0, inverter: 0 });
  const [selectedMeters, setSelectedMeters] = useState([]);
  const [selectedInverters, setSelectedInverters] = useState([]);
  const [range, setRange] = useState(RANGES[1]);
  const [timezone, setTimezone] = useState(TIMEZONES[0]);
  const [unit, setUnit] = useState(UNITS[1]);
  const [interval, setInterval] = useState(INTERVALS[0]);

  const fetchRawData = (range) => {
    if (selectedMeters.length === 0) {
      toastr.warning({ title: 'Select at least one meter' });
      setChartData([]);
      return;
    }

    setLoading(true);
    Promise.all(
      map(concat(selectedMeters, selectedInverters), async (device) => {
        const deviceId = get(device, `${device.type_}_id`);
        return {
          device_id: deviceId,
          records: await new WebAPIClient().GET(
            `/resource/timestream/${
              device.org_id
            }/${range.start.valueOf()}/${range.end.valueOf()}`,
            { deviceId }
          ),
        };
      })
    )
      .then((payloads) => {
        setRawData(payloads);
      })
      .catch((err) => {
        errorResponseToastr(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (meters.length <= 5) {
      setSelectedMeters(meters);
    } else {
      const _meters = sortBy(meters, 'name');
      setSelectedMeters(_meters.slice(0, 5));
    }
  }, [meters]);

  // set timezone
  useEffect(() => {
    if (!isEmpty(defaultTimezone)) {
      setTimezone(defaultTimezone);
    }
  }, [defaultTimezone]);

  // update range, after timezone updates
  useEffect(() => {
    if (timezone?.offset) {
      setRange((r) => {
        if (r.key === 6) {
          return { ...r, offset: timezone.offset };
        } else {
          return { ...r, ...r.func(timezone.zone), offset: timezone.offset };
        }
      });
    }
  }, [timezone]);

  // compile chartData after rawData updates
  useEffect(() => {
    if (!loading && !isEmpty(rawData)) {
      setChartData(compileChartData(rawData, orgId, range, unit, interval));
    }
  }, [rawData, orgId, range, unit, interval, loading]);

  // set default chart data
  useEffect(() => {
    if (
      chartData.length === 0 &&
      range.start &&
      range.end &&
      timeseriesData.length > 0
    ) {
      const start = range.start.unix();
      const end = range.end.unix();

      let _data = map(selectedMeters, (meter) => {
        let selectedTimeseries = find(timeseriesData, (ts) => {
          return ts.device_id === meter.meter_id && ts.data_type === 'recent';
        });
        return {
          device_id: meter.meter_id,
          records: filter(selectedTimeseries?.data, (record) => {
            return record.timestamp >= start && record.timestamp <= end;
          }),
        };
      });
      if (_data.length > 0) {
        setRawData(_data);
      }
    }
  }, [selectedMeters, chartData, range, timeseriesData]);

  useEffect(() => {
    if (!isEmpty(chartData)) {
      setTotals(calculateTotals(chartData, unit));
    }
  }, [chartData, unit]);

  const handleIntervalChange = (value) => {
    if (value.minutes === 15) {
      setUnit(UNITS[1]);
    } else {
      setUnit(UNITS[4]);
    }
    setInterval(value);
  };

  const handleSelectRange = (newRange, fetchData) => {
    setRange(newRange);
    if (fetchData) {
      fetchRawData(newRange);
    }
  };

  return (
    <>
      <ChartHeader>
        <SelectMeters
          allMeters={meters}
          selectedMeters={selectedMeters}
          setSelectedMeters={setSelectedMeters}
        />
        <SelectInverters
          allInverters={inverters}
          selectedInverters={selectedInverters}
          setSelectedInverters={setSelectedInverters}
        />

        <SelectRange
          range={range}
          setRange={handleSelectRange}
          timezone={timezone}
        />

        <SelectTimezone
          selectedTimezone={timezone}
          setSelectedTimezone={setTimezone}
        />

        <SelectInterval
          selectedInterval={interval}
          setSelectedInterval={handleIntervalChange}
        />

        <SelectUnit
          selectedUnit={unit}
          setSelectedUnit={setUnit}
          units={UNITS}
        />

        <Tooltip title='Fetch Data' placement='top'>
          <IconButton onClick={() => fetchRawData(range)}>
            <FontAwesomeIcon icon={['fal', 'cloud-download']} />
          </IconButton>
        </Tooltip>
        <GenerateCSVButton
          generateCsvString={() =>
            prepareCsvData(chartData, selectedMeters, selectedInverters, unit)
          }
          filename={`Production Profile (${dayjs().format('ddd MMM DD YYYY')})`}
        />
      </ChartHeader>

      <Grid size={{ xs: 12 }}>
        <Card raised sx={{ px: 1, py: 0.5 }}>
          <ProductionProfileChart
            chartData={chartData}
            meters={selectedMeters}
            inverters={selectedInverters}
            range={range}
            timezone={timezone}
            unit={unit}
            loading={loading}
            fetchRawData={fetchRawData}
          />
        </Card>
      </Grid>
      <Grid
        size={{ xs: 12 }}
        sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <List sx={{ width: '100%', maxWidth: 350 }}>
          {selectedMeters.length > 0 && (
            <ListItem>
              <ListItemText secondary='Total Meter Production: ' />
              <ListItemText
                primary={loading ? '-' : displaykW(totals.meter, true)}
                slotProps={{
                  primary: {
                    fontWeight: 'bold',
                    align: 'right',
                    sx: { color: (theme) => theme.veregy_colors.blue },
                  },
                }}
              />
            </ListItem>
          )}
          {selectedInverters.length > 0 && (
            <ListItem>
              <ListItemText secondary='Total Inverter Production: ' />
              <ListItemText
                primary={loading ? '-' : displaykW(totals.inverter, true)}
                slotProps={{
                  primary: {
                    fontWeight: 'bold',
                    align: 'right',
                    sx: { color: (theme) => theme.veregy_colors.orange },
                  },
                }}
              />
            </ListItem>
          )}
        </List>
      </Grid>
    </>
  );
}

ProductionProfileChartContainer.propTypes = {
  defaultTimezone: PropTypes.object,
  meters: PropTypes.array,
  inverters: PropTypes.array,
};

export default ProductionProfileChartContainer;
